import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { aiModelsService } from '../../services/ai-models.service';
import type { AiModelListParams } from '../../services/ai-models.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { AiModelWithId } from '../../types';
import ConfigurationStatusTag from './configuration-status-tag';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import { ProjectCell } from '../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import { formatDate } from 'sharedUi/dateUtils';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [
  { field: 'displayName', header: 'Display Name', filter: true },
  { field: 'modelId', header: 'Model ID', filter: true },
  { field: 'providerType', header: 'Provider', filter: true },
  { field: 'modelType', header: 'Model Type', filter: true },
];

const AiModelList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'ai-model',
    basePath: '/ai-models',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.AI_MODEL_MASTER) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.AI_MODEL_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.AI_MODEL_MASTER);

  const [models, setModels] = useState<AiModelWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  // Deleted records are never shown in the list UI (the toggle was removed).
  const [includeDeleted] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  // >1 accessible project ≈ platform/partner admin who may filter + re-scope.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states for server-side sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  // Debounce search text (500ms delay)
  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 10000,
    });
  }, []);

  const fetchModels = useCallback(
    async (
      page: number = 1,
      size: number = pageSize,
      sortBy: string | null = sortField,
      order: 1 | -1 | null = sortOrder,
      activeFilters: Record<string, any> = filters
    ) => {
      try {
        setLoading(true);

        const params: AiModelListParams = {
          page,
          page_size: size,
          include_deleted: includeDeleted,
        };

        if (debouncedSearchText && debouncedSearchText.trim()) {
          params.search = debouncedSearchText.trim();
        }
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = order === 1 ? 'asc' : 'desc';
        }
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;

        if (selectedProjectId) params.app_id = selectedProjectId;
        params.include_global = showCore;

        const response = await aiModelsService.list(params);
        const mappedData = (response.data || []).map((model) => ({ ...model, id: model._id }));
        // Default view (no explicit column sort): surface Configured models above
        // Not-configured / Broken ones, so the models that can actually run sit at
        // the top. `configurationStatus` is computed server-side (not sortable there),
        // so we order the page here. Stable sort keeps the server order within a rank.
        if (!sortBy) {
          const rank = (r: AiModelWithId) => {
            const s = (r as any).configurationStatus?.status;
            return s === 'configured' ? 0 : s === 'missing_credentials' ? 1 : s === 'unresolvable' ? 2 : 3;
          };
          mappedData.sort((a, b) => rank(a) - rank(b));
        }
        setModels(mappedData);

        const total = (response as any).pagination?.totalItems ?? (response as any).pagination?.total ?? response.total ?? 0;
        setTotalRecords(total);
      } catch (error) {
        console.error('Failed to fetch AI models:', error);
        showError(extractErrorMessage(error, 'Failed to load AI models'));
      } finally {
        setLoading(false);
      }
    },
    [includeDeleted, debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]
  );

  // Handle page change from DataTable
  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchModels(newPage, event.rows, sortField, sortOrder);
  };

  // Handle sort change from DataTable
  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setFirst(0);
    fetchModels(1, pageSize, newSortField, newSortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchModels(1, pageSize, sortField, sortOrder, next);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setFirst(0);
    fetchModels(1, pageSize);
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (id: string | number) => {
    navigateToView(String(id));
  };

  const handleEdit = (id: string | number) => {
    navigateToEdit(String(id));
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedModelId(id as string);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedModelId) return;

    try {
      setDeleteLoading(true);
      await aiModelsService.softDelete(selectedModelId);
      setDeleteDialogOpen(false);
      setSelectedModelId(null);
      fetchModels(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (error: any) {
      console.error('Failed to delete AI model:', error);
      setDeleteDialogOpen(false);
      setSelectedModelId(null);
      showError(extractErrorMessage(error, 'Failed to delete AI model'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedModelId(null);
  };

  const columns: SaasColumn<AiModelWithId>[] = useMemo(() => {
    const cols: SaasColumn<AiModelWithId>[] = [
      { field: 'displayName', header: 'Display Name', sortable: true, filter: true, hideable: false },
      { field: 'modelId', header: 'Model ID', sortable: true, filter: true, body: (r) => r.modelId || '-' },
      { field: 'providerType', header: 'Provider', sortable: true, filter: true },
      { field: 'modelType', header: 'Model Type', sortable: true, filter: true, body: (r) => r.modelType || '-' },
      { field: 'version', header: 'Version', sortable: true, body: (r) => r.version || '-' },
      {
        // A model with no usable credential used to be indistinguishable from a
        // working one on this screen; the only way to find out was to run a
        // prompt and read the 503.
        field: 'configurationStatus',
        header: 'Credentials',
        body: (r) => <ConfigurationStatusTag status={r.configurationStatus} id={r._id} />,
      },
      { field: 'createdAt', header: 'Created At', sortable: true, body: (r) => formatDate(r.createdAt) },
    ];

    if (includeDeleted) {
      cols.push({
        field: 'status',
        header: 'Status',
        body: (r) => {
          const deleted = (r as any).isDelete || r.isDeleted;
          return (
            <span
              className={`saas-status-dot ${deleted ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {deleted ? 'Deleted' : 'Active'}
            </span>
          );
        },
      });
    }

    cols.push({
      field: 'appId',
      header: 'Project',
      body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
    });

    cols.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (r) => {
        const deleted = (r as any).isDelete || r.isDeleted;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleView(r._id)}
              data-testid="AiModelList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
            </button>
            {!deleted && (
              <>
                {canUpdate && (
                  <button aria-label="Edit"
                    className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    onClick={() => handleEdit(r._id)}
                    data-testid="AiModelList.Action.Edit"
                    data-pr-tooltip="Edit"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
                {canDelete && (
                  <button aria-label="Delete"
                    className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => handleDeleteClick(r._id)}
                    data-testid="AiModelList.Action.Delete"
                    data-pr-tooltip="Delete"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [canUpdate, canDelete, canManageProjects, includeDeleted, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.AI_MODEL_MASTER}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-AiModel-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-server"
          title="AI Models"
          subtitle="Manage AI model registry, capabilities, and pricing"
          testId="AiModel.Header"
        />

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="ai-model"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={models}
            loading={loading}
            dataKey="_id"
            emptyMessage="No AI models found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search AI models..."
            testId="AiModelList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                    data-testid="AiModelList.Filter.Project"
                  />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="AiModelList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <RefreshButton
                  onClick={() => fetchModels(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                  testId="AiModelList.Button.Refresh"
                />
                {canCreate && (
                  <Button
                    label="Create Model"
                    icon="pi pi-plus"
                    onClick={() => navigateToCreate()}
                    testId="AiModelList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this AI model? This will mark it as deleted."
          testId="AiModelList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default AiModelList;
